/**
 * MeetingsList — TASK-666.
 *
 * The account's meetings as a summonable canvas widget, replacing the routed
 * `/meetings` page. Per `docs/generative-canvas/DASHBOARD-WIDGETIZATION.md` this is the
 * **list/book archetype**: account-scoped, self-fetching, prop-driven filters, no global
 * store — the same shape as `ClientBook`.
 *
 * Honest state model (TASK-662 definitions). Preparation, capture, analysis and
 * documentation are SEPARATE concepts and get separate, text-bearing badges. The old page
 * showed a single "Brief" chip, which said nothing about whether the meeting was actually
 * recorded or analysed. Every badge carries a word — no status is colour-only.
 *
 * Truncation is stated, not hidden: `GET /meeting` caps at 100 rows, so when the cap is
 * hit the widget says so and points at the filters rather than implying the list is
 * complete.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Calendar, FileText, Plus, RefreshCw } from 'lucide-react';

import {
  listMeetings,
  cancelMeeting,
  dispatchMeetingBot,
  type MeetingListItem,
} from '../../../services/api/meetingApi';
import { MEETINGS_QUERY_KEY } from '../../../services/api/queryKeys';
import {
  ANALYSIS_STATE_LABELS,
  ANALYSIS_STATE_STYLES,
  CAPTURE_SOURCE_LABELS,
  MEETING_TYPE_LABELS,
  STATUS_STYLES,
} from '../../meeting/meetingRouting';
import { CreateMeetingModal, type MeetingCreationData } from '../../meeting/CreateMeetingModal';
import { CaptureModeChooser } from '../../meeting/CaptureModeChooser';
import { RescheduleModal } from '../../meeting/RescheduleModal';
import { MeetingRowActions } from '../../meeting/MeetingRowActions';
import { toast } from 'sonner';
import { httpClient } from '../../../lib/httpClient';
import { WidgetContainer } from '../WidgetContainer';
import { useCanvasActions } from '../../../stores/canvasStore';
import { ALERT_PAGE_SIZE, useAlertStore } from '../../../stores/alertStore';
import {
  getCalendarWindow,
  materializeCalendarEvent,
  type CalendarEvent,
} from '../../../services/api/calendarMeetingApi';

/** The endpoint's hard cap (meeting_routes.py `.limit(100)`). */
const ROW_CAP = 100;

export type MeetingsTimeframe = 'upcoming' | 'past' | 'all';

interface MeetingsListProps {
  /**
   * TASK-707 — narrow to ONE client's meetings. Optional by design: with no `clientId` this
   * stays the book-level view TASK-666 requires ("'my meetings' must never demand a client").
   * Server-injected from the resolved focus, exactly like a client-scoped widget — the model
   * never emits an id.
   */
  clientId?: string;
  clientName?: string;
  status?: string[];
  timeframe?: MeetingsTimeframe;
  hasBrief?: boolean;
  /**
   * TASK-673: when summoned from a reminder alert's "Start meeting" action
   * (`/meeting/:id/start`), open the capture chooser for this session as soon as the list
   * loads — so the deep-link lands on the canvas, not a routed page.
   */
  autoStartSessionId?: string;
  /**
   * TASK-672: when summoned from an overdue-reminder alert's recovery action
   * (`/meeting/:id/reschedule` or `/meeting/:id/cancel`), open the matching modal for this
   * session once the list loads — same summon-redirect pattern as `autoStartSessionId`.
   */
  autoRescheduleSessionId?: string;
  autoCancelSessionId?: string;
}

/**
 * TASK-668 — a meeting that is over, captured nothing, and has no approved record yet.
 * This is the realistic in-person case: nothing on the table during the conversation, so
 * the only way it becomes a client record is a dictation afterwards.
 */
function needsDictation(m: MeetingListItem): boolean {
  const status = m.status.toLowerCase();
  const finished = status === 'completed' || status === 'post_processing';
  const captured = Boolean(m.capture_source && m.capture_source !== 'none');
  return finished && !captured && !m.documented_at;
}

/** Lifecycle states that mean the meeting has already happened. */
const FINISHED = new Set(['completed', 'post_processing', 'cancelled']);

/** TASK-673 — a meeting the RM can still start: scheduled or undated-but-unfinished, and
 *  not already live or over. These rows expose an explicit Start action. */
function isStartable(m: MeetingListItem): boolean {
  const s = m.status.toLowerCase();
  return !FINISHED.has(s) && s !== 'live' && s !== 'in_progress';
}

function briefState(m: MeetingListItem): {
  label: string;
  description: string;
  className: string;
} {
  if (m.has_brief) {
    return {
      label: 'Brief ready',
      description: 'Open preparation to review it',
      className: 'bg-green-100 text-green-800',
    };
  }
  if (m.preparation_status === 'brief_generation_in_progress') {
    return {
      label: 'Brief generating',
      description: 'Preparation is still running',
      className: 'bg-blue-100 text-blue-800',
    };
  }
  if (
    m.preparation_status === 'in_progress' ||
    m.preparation_status === 'insight_gathering_in_progress' ||
    m.preparation_status === 'insight_gathering_complete'
  ) {
    return {
      label: 'Preparation started',
      description: 'Continue preparing this meeting',
      className: 'bg-amber-100 text-amber-800',
    };
  }
  return {
    label: 'Brief not started',
    description: 'Generate it from meeting preparation',
    className: 'bg-gray-100 text-gray-700',
  };
}

/**
 * Timeframe split uses the browser's timezone, which is the RM's configured one in this
 * app (no separate per-user tz setting exists).
 *
 * Undated meetings (`scheduled_at === null`) are real and common — the demo book is
 * entirely undated. Hiding them from BOTH timeframes made the default view render "no
 * meetings" while meetings plainly existed, which is exactly the kind of dishonesty this
 * epic is removing. So they are bucketed by LIFECYCLE instead of by date: an unfinished
 * meeting is still ahead of you, a finished one is behind you. They sort last within
 * their bucket (see `sortForTimeframe`) and render their date as "—", so they are never
 * presented as though they had a time.
 */
function inTimeframe(m: MeetingListItem, timeframe: MeetingsTimeframe, now: number): boolean {
  if (timeframe === 'all') return true;
  const finished = FINISHED.has(m.status.toLowerCase());
  if (!m.scheduled_at) return timeframe === 'upcoming' ? !finished : finished;
  const t = new Date(m.scheduled_at).getTime();
  return timeframe === 'upcoming' ? t >= now : t < now;
}

function sortForTimeframe(rows: MeetingListItem[], timeframe: MeetingsTimeframe) {
  const sorted = [...rows];
  sorted.sort((a, b) => {
    // Undated rows always sort last — they cannot claim a position on the timeline.
    if (!a.scheduled_at && !b.scheduled_at) return a.client_name.localeCompare(b.client_name);
    if (!a.scheduled_at) return 1;
    if (!b.scheduled_at) return -1;
    const at = new Date(a.scheduled_at).getTime();
    const bt = new Date(b.scheduled_at).getTime();
    // Upcoming: nearest first. Past/all: newest first.
    return timeframe === 'upcoming' ? at - bt : bt - at;
  });
  return sorted;
}

export function MeetingsList({
  clientId,
  clientName,
  status,
  timeframe = 'upcoming',
  hasBrief,
  autoStartSessionId,
  autoRescheduleSessionId,
  autoCancelSessionId,
}: MeetingsListProps) {
  const { addSpecs } = useCanvasActions();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // TASK-673: the meeting whose capture mode is being chosen (null = chooser closed).
  const [startTarget, setStartTarget] = useState<MeetingListItem | null>(null);
  // TASK-677: reschedule/cancel recovery targets.
  const [rescheduleTarget, setRescheduleTarget] = useState<MeetingListItem | null>(null);
  const [cancelTarget, setCancelTarget] = useState<MeetingListItem | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // TASK-676: the session whose bot is being dispatched (for the button busy state).
  const [dispatchingBot, setDispatchingBot] = useState<string | null>(null);
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);

  // Deep review #4: the key is deliberately CONSTANT (it used to claim it keyed on the filter
  // props, which it never did). `listMeetings` takes no arguments — every filter prop is applied
  // client-side below — so one cache entry serves every tile, and two tiles with different
  // filters stay independent because they filter the shared result, not because they refetch.
  //
  // TASK-877: that reasoning was right and stopped one tile short. `useLiveMeetingSurfacing`
  // fetches the SAME argument-less endpoint under `['live-meetings']`, so the constant key
  // deduplicated tiles against each other while still running a second fetch stream against
  // the hook — `GET /meeting` was measured 8+ times per load. Both now use the shared
  // `MEETINGS_QUERY_KEY`, which is the one cache this one resource should have had.
  const query = useQuery({
    queryKey: MEETINGS_QUERY_KEY,
    queryFn: listMeetings,
    staleTime: 30_000,
  });

  const all = useMemo(() => query.data ?? [], [query.data]);

  // TASK-673: reminder deep-link — open the capture chooser for the requested session once
  // the list has loaded. Fires once; if the session is not in the list it degrades to the
  // plain list rather than erroring.
  const [autoStartDone, setAutoStartDone] = useState(false);
  useEffect(() => {
    if (!autoStartSessionId || autoStartDone || !all.length) return;
    const row = all.find((m) => m.session_id === autoStartSessionId);
    if (row) setStartTarget(row);
    setAutoStartDone(true);
  }, [autoStartSessionId, autoStartDone, all]);

  // TASK-672: overdue-reminder recovery deep-links — open the reschedule/cancel modal for the
  // requested session once loaded. Fires once; a missing session degrades to the plain list.
  const [autoRecoveryDone, setAutoRecoveryDone] = useState(false);
  useEffect(() => {
    const sid = autoRescheduleSessionId || autoCancelSessionId;
    if (!sid || autoRecoveryDone || !all.length) return;
    const row = all.find((m) => m.session_id === sid);
    if (row) {
      if (autoRescheduleSessionId) setRescheduleTarget(row);
      else setCancelTarget(row);
    }
    setAutoRecoveryDone(true);
  }, [autoRescheduleSessionId, autoCancelSessionId, autoRecoveryDone, all]);

  const rows = useMemo(() => {
    const now = Date.now();
    const statusSet = status?.length ? new Set(status.map((s) => s.toLowerCase())) : null;
    const filtered = all.filter((m) => {
      // TASK-707: the client filter, when one was named. Narrows client-side over the same
      // RLS-scoped `GET /meeting` payload as every other filter here.
      if (clientId && m.client_id !== clientId) return false;
      if (statusSet && !statusSet.has(m.status.toLowerCase())) return false;
      if (hasBrief !== undefined && Boolean(m.has_brief) !== hasBrief) return false;
      return inTimeframe(m, timeframe, now);
    });
    return sortForTimeframe(filtered, timeframe);
  }, [all, clientId, status, hasBrief, timeframe]);

  const truncated = all.length >= ROW_CAP;

  // TASK-669: calendar-only events. Kept in a SEPARATE query so a Graph outage degrades
  // the calendar strip alone and never takes the Kielis meetings list down with it.
  const calendar = useQuery({
    queryKey: ['calendar-window'],
    queryFn: getCalendarWindow,
    staleTime: 60_000,
    retry: false,
  });
  const calendarEvents = calendar.data?.events ?? [];
  const calendarProblem =
    calendar.data && !['ok', 'empty'].includes(calendar.data.status) ? calendar.data : null;

  const [materializing, setMaterializing] = useState<string | null>(null);

  /**
   * Open a meeting ON the canvas — MeetingsList is itself a canvas widget, so a row must
   * summon its sibling widget, not route out to a full page (TASK-662: "never on a route
   * by accident"). Only the post-meeting review has no canvas widget yet, so it still routes.
   */
  function openMeeting(m: MeetingListItem) {
    const status = m.status.toLowerCase();
    if (status === 'live' || status === 'in_progress') {
      // TASK-673: a bot-owned meeting opens the passive monitor; an in-person recording
      // opens the recorder. Before this, every live row summoned the bot monitor even for
      // browser-mic meetings, so an in-person recording had no live surface.
      if (m.meeting_url || m.capture_source === 'attendee-bot') {
        addSpecs([
          { component: 'LiveMeetingNow', props: { sessionId: m.session_id, clientName: m.client_name } },
        ]);
      } else if (m.capture_mode === 'none') {
        // TASK-700: "in person — don't record, dictate a summary after" never reaches the
        // recorder, so offering one is offering a surface the RM will never use. Dictation
        // is the surface this mode is waiting on, and approving it is what closes the meeting.
        //
        // This branches on `capture_mode` — the RM's RECORDED INTENT — and deliberately not
        // on `capture_source`, which the ticket suggested. `capture_source` is derived from
        // `utterances.source`, so a browser-mic meeting also reads 'none' until its first
        // captured word; branching on it would send a recording meeting here and, because
        // utterances only exist once the recorder has run, it could never get back.
        addSpecs([
          {
            component: 'VoiceNoteWidget',
            props: { clientId: m.client_id, clientName: m.client_name, sessionId: m.session_id },
          },
        ]);
      } else {
        // TASK-673: in-person capture stays ON the canvas — summon the recorder tile rather
        // than routing out to a full page.
        addSpecs([
          {
            component: 'MeetingRecorderWidget',
            props: { sessionId: m.session_id, clientId: m.client_id, clientName: m.client_name },
          },
        ]);
      }
    } else if (status === 'completed' || status === 'post_processing') {
      // TASK-673: review is a canvas tile now — stay on the canvas.
      addSpecs([
        {
          component: 'PostMeetingReviewWidget',
          props: { sessionId: m.session_id, clientName: m.client_name },
        },
      ]);
    } else {
      // TASK-671: preparation is bound to THIS session, never resolved from client-level data.
      addSpecs([{ component: 'MeetingPrep', props: { sessionId: m.session_id, clientId: m.client_id } }]);
    }
  }

  /** Create the session on demand, then open it on the canvas. */
  async function act(ev: CalendarEvent, intent: 'prepare' | 'dictate') {
    if (!ev.client_id) return; // needs_client rows must pick a client first
    setMaterializing(ev.event_id);
    try {
      const { session_id } = await materializeCalendarEvent({
        calendar_event_id: ev.event_id,
        client_id: ev.client_id,
        subject: ev.subject,
        scheduled_at: ev.start,
      });
      query.refetch();
      calendar.refetch();
      if (intent === 'prepare')
        // TASK-671: prepare the materialized session, not a client-level guess.
        addSpecs([{ component: 'MeetingPrep', props: { sessionId: session_id, clientId: ev.client_id } }]);
      else
        addSpecs([
          {
            component: 'VoiceNoteWidget',
            props: { clientId: ev.client_id, clientName: ev.client_name, sessionId: session_id },
          },
        ]);
    } finally {
      setMaterializing(null);
    }
  }

  async function sendBot(m: MeetingListItem) {
    setDispatchingBot(m.session_id);
    try {
      await dispatchMeetingBot(m.session_id);
      toast.success('Recording bot is joining the meeting');
      // Show the live monitor so the RM sees the bot's join/capture status.
      addSpecs([
        { component: 'LiveMeetingNow', props: { sessionId: m.session_id, clientName: m.client_name } },
      ]);
      query.refetch();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Could not dispatch the recording bot. Please try again.');
    } finally {
      setDispatchingBot(null);
    }
  }

  /**
   * Deep review #6: `/cancel` and `/reschedule` resolve the meeting's reminder rows inside the
   * request, but nothing tells the open dashboard. Deep review #4 closed the radar-sweep path
   * with a `radar_updated` refetch — that frame only arrives on the next tick, 15 minutes out
   * (`RADAR_REFRESH_INTERVAL_MIN`), so the RM who just cancelled a meeting went on staring at
   * its "Overdue meeting" card. The truth is already committed server-side; just re-read it.
   */
  const refreshAlerts = () =>
    useAlertStore.getState().fetchAlerts({ limit: ALERT_PAGE_SIZE });

  async function confirmCancel() {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await cancelMeeting(cancelTarget.session_id);
      toast.success('Meeting cancelled');
      setCancelTarget(null);
      query.refetch();
      refreshAlerts();
    } catch {
      toast.error('Could not cancel the meeting. Please try again.');
    } finally {
      setCancelling(false);
    }
  }

  const handleCreate = async (data: MeetingCreationData) => {
    setCreating(true);
    try {
      // TASK-671: `POST /meeting/create` returns `session_id` at the TOP LEVEL, and
      // httpClient already unwraps `response.data`. The old `result.data.session_id` read a
      // second `data` layer that does not exist, so `sid` was always undefined and
      // preparation never opened. Open the exact session that was just created.
      const result = await httpClient.post<{ session_id?: string }>('/meeting/create', data);
      setCreateOpen(false);
      const sid = result?.session_id;
      query.refetch();
      if (sid)
        addSpecs([
          {
            component: 'MeetingPrep',
            props: { sessionId: sid, clientId: data.client_id, createdNow: true },
          },
        ]);
    } finally {
      setCreating(false);
    }
  };

  // TASK-707: when the list is narrowed to one client, SAY so in the heading. A filtered list
  // that looks like the whole book is how "no meetings" gets misread as "none exist".
  return (
    <WidgetContainer
      // TASK-871: derived, never supplied. A caller-set heading over this fixed filter set is
      // a claim the widget cannot back — see filterContract.ts.
      title={clientName ? `Meetings — ${clientName}` : 'Meetings'}
    >
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {timeframe === 'upcoming'
              ? 'Upcoming meetings, nearest first'
              : timeframe === 'past'
                ? 'Past meetings, newest first'
                : 'All meetings, newest first'}
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={query.isFetching}
              aria-label="Refresh meetings"
              className="rounded p-1.5 text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1 rounded-lg bg-kielis-navy px-3 py-1.5 text-xs font-medium text-white hover:bg-kielis-navy/90"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              New meeting
            </button>
          </div>
        </div>

        {query.isLoading && (
          <p role="status" aria-live="polite" className="py-8 text-center text-sm text-muted-foreground">
            Loading meetings…
          </p>
        )}

        {query.isError && !query.isLoading && (
          <div role="status" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Meetings could not be loaded.{' '}
            <button type="button" onClick={() => query.refetch()} className="underline hover:no-underline">
              Retry
            </button>
          </div>
        )}

        {!query.isLoading && !query.isError && rows.length === 0 && (
          <div className="rounded-lg border border-border p-8 text-center">
            <Calendar className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {all.length === 0
                ? 'No meetings yet.'
                : `No ${timeframe === 'all' ? '' : timeframe} meetings match these filters.`}
            </p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="mt-3 text-sm font-medium text-kielis-navy underline hover:no-underline"
            >
              Create a meeting
            </button>
          </div>
        )}

        {rows.length > 0 && (
          <div className="space-y-2" role="list" aria-label="Meetings for this account">
            {rows.map((m) => {
              const brief = briefState(m);
              const status = m.status.toLowerCase();
              const live = status === 'live' || status === 'in_progress';
              const reviewable = status === 'completed' || status === 'post_processing';
              const dictate = needsDictation(m);
              return (
                <article
                  key={m.session_id}
                  role="listitem"
                  className="rounded-xl border border-border bg-background p-3 transition-colors hover:bg-muted/20"
                  data-testid="meeting-row"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-foreground">
                          <button
                            type="button"
                            onClick={() => openMeeting(m)}
                            aria-label={`Open meeting: ${m.client_name}${m.meeting_goal ? ` — ${m.meeting_goal}` : ''}`}
                            className="text-left underline-offset-2 hover:underline"
                          >
                            {m.client_name}
                          </button>
                        </h3>
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                      <p
                        data-testid="meeting-goal"
                        className="mt-1 text-sm text-foreground"
                      >
                        {m.meeting_goal?.trim() || 'No goal set for this meeting'}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {m.scheduled_at
                          ? format(new Date(m.scheduled_at), 'EEE, MMM d · HH:mm')
                          : '— Not scheduled'}
                        {' · '}
                        {MEETING_TYPE_LABELS[m.meeting_type] ?? m.meeting_type}
                      </p>
                    </div>

                    <MeetingRowActions
                      meeting={m}
                      startable={isStartable(m)}
                      live={live}
                      reviewable={reviewable}
                      dictate={dictate}
                      dispatchingBot={dispatchingBot}
                      openMenuSessionId={menuSessionId}
                      onToggleMenu={(sid) =>
                        setMenuSessionId((current) => (current === sid ? null : sid))
                      }
                      onCloseMenu={() => setMenuSessionId(null)}
                      onStart={setStartTarget}
                      onOpen={openMeeting}
                      onDictate={(row) =>
                        addSpecs([
                          {
                            component: 'VoiceNoteWidget',
                            props: {
                              clientId: row.client_id,
                              clientName: row.client_name,
                              sessionId: row.session_id,
                            },
                          },
                        ])
                      }
                      onReschedule={setRescheduleTarget}
                      onCancel={setCancelTarget}
                      onSendBot={(row) => void sendBot(row)}
                    />
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                    <button
                      type="button"
                      data-testid="prepare-action"
                      onClick={() => openMeeting(m)}
                      className="flex min-w-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-muted"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <span>
                        <span
                          data-testid="brief-badge"
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${brief.className}`}
                        >
                          {brief.label}
                        </span>
                        <span className="ml-2 text-xs text-muted-foreground">{brief.description}</span>
                      </span>
                    </button>
                    {m.capture_source && m.capture_source !== 'none' && (
                      <span data-testid="capture-badge" className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                        {CAPTURE_SOURCE_LABELS[m.capture_source] ?? m.capture_source}
                      </span>
                    )}
                    {m.analysis_state && m.analysis_state !== 'not_started' && (
                      <span
                        data-testid="analysis-badge"
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          ANALYSIS_STATE_STYLES[m.analysis_state] ?? 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {ANALYSIS_STATE_LABELS[m.analysis_state] ?? m.analysis_state}
                      </span>
                    )}
                    {m.documented_at && (
                      <span data-testid="documented-badge" className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Documented
                      </span>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* TASK-669 — calendar-only rows, visually distinct from Kielis sessions because
            they are NOT yet part of the client record. Acting on one materializes it. */}
        {(calendarEvents.length > 0 || calendarProblem) && (
          <div className="rounded-lg border border-dashed border-border p-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">From your calendar</p>

            {calendarProblem ? (
              <p role="status" className="text-xs text-amber-800">
                {calendarProblem.message}
              </p>
            ) : (
              <ul className="space-y-2">
                {calendarEvents.map((ev) => (
                  <li
                    key={ev.event_id}
                    data-testid="calendar-row"
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      Calendar only
                    </span>
                    <span className="text-foreground">{ev.subject}</span>
                    {ev.start && (
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(ev.start), 'MMM d, HH:mm')}
                      </span>
                    )}
                    {ev.classification === 'needs_client' ? (
                      <span className="text-xs text-amber-800">Select client to use this</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          data-testid="calendar-prepare"
                          disabled={materializing === ev.event_id}
                          onClick={() => act(ev, 'prepare')}
                          className="rounded-full border border-kielis-navy/30 px-2 py-0.5 text-xs font-medium text-kielis-navy hover:bg-kielis-navy/5 disabled:opacity-50"
                        >
                          Prepare
                        </button>
                        <button
                          type="button"
                          data-testid="calendar-dictate"
                          disabled={materializing === ev.event_id}
                          onClick={() => act(ev, 'dictate')}
                          className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50"
                        >
                          Dictate summary
                        </button>
                        {!ev.has_conference_url && (
                          <span className="text-[11px] text-muted-foreground">
                            No conference link — a bot cannot join
                          </span>
                        )}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {truncated && (
          <p className="text-xs text-amber-800" role="note">
            Showing the most recent {ROW_CAP} meetings — this list is not complete. Narrow
            it with a status or timeframe filter to see the rest.
          </p>
        )}
      </div>

      <CreateMeetingModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onCreateMeeting={handleCreate}
        isLoading={creating}
      />

      {startTarget && (
        <CaptureModeChooser
          sessionId={startTarget.session_id}
          clientId={startTarget.client_id}
          clientName={startTarget.client_name}
          meetingGoal={startTarget.meeting_goal}
          meetingUrl={startTarget.meeting_url}
          onClose={() => setStartTarget(null)}
        />
      )}

      {rescheduleTarget && (
        <RescheduleModal
          sessionId={rescheduleTarget.session_id}
          clientName={rescheduleTarget.client_name}
          currentIso={rescheduleTarget.scheduled_at}
          onClose={() => setRescheduleTarget(null)}
          onRescheduled={() => { query.refetch(); refreshAlerts(); }}
        />
      )}

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Cancel meeting"
            className="w-full max-w-sm rounded-2xl border border-border bg-background p-6 shadow-xl"
          >
            <h2 className="text-lg font-semibold text-foreground">Cancel this meeting?</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {cancelTarget.client_name}
              {cancelTarget.meeting_goal ? ` — ${cancelTarget.meeting_goal}` : ''}. This cannot be
              undone; any recording bot is removed.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                className="rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Keep meeting
              </button>
              <button
                type="button"
                data-testid="confirm-cancel"
                onClick={confirmCancel}
                disabled={cancelling}
                className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {cancelling ? 'Cancelling…' : 'Cancel meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}

export default MeetingsList;
